import React, { useEffect, useState } from 'react';
import HistoryLog from '../HistoryLog';
import T from 'Components/Helpers/translate';
import TimelineGenericRender, { TimelineConfig } from 'Components/TimelineRow/TimelineGeneric/TimelineCardConfigs';
import Client from 'App/resources/Model/Client';
import Contact from 'App/resources/Model/Contact';
import TimelineLoadMore from 'Components/TimelineRow/TimelineLoadMore/TimelineLoadMore';
import FlowContactsActive from 'App/babel/resources/FlowContactsActive';
import { ActiveFlowEvent } from 'App/resources/Model/Event';
import logError from 'Helpers/logError';

type Props = {
	client: Client;
	// When set, active flows are fetched for the contact instead of the client
	contact?: Contact;
};

const FLOW_LIMIT = 10;

const ActiveFlowHistoryLog = ({ client, contact }: Props) => {
	const [flows, setFlows] = useState<ActiveFlowEvent[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [offset, setOffset] = useState(0);

	const loadFlows = (reset = false) => {
		setLoading(true);
		const currentOffset = reset ? 0 : offset;
		FlowContactsActive.getActiveFlows(
			contact ? 'contact' : 'client',
			contact ? contact.id : client.id,
			FLOW_LIMIT,
			currentOffset
		)
			.then((res: { data: any[]; metadata: { total: number } }) => {
				const newFlows: ActiveFlowEvent[] = res.data.map(
					({ flowId, flowName, date, contacts, segmentId, regByName }) => ({
						flowName,
						date,
						contacts: contacts.split('_##_').map((contactStr: string) => {
							const parts = contactStr.split(',-,');
							return { name: parts[0], id: Number(parts[1]) };
						}),
						segmentId,
						entityType: 'ActiveFlow' as const,
						entityId: flowId,
						regByName,
						users: []
					})
				);
				setFlows(prev => (reset ? newFlows : [...prev, ...newFlows]));
				setTotal(res.metadata.total);
				setOffset(currentOffset + FLOW_LIMIT);
				setLoading(false);
			})
			.catch((err: unknown) => {
				logError(err, 'ActiveFlowHistoryLog - loadFlows');
				setLoading(false);
			});
	};

	useEffect(() => {
		loadFlows(true);
	}, [contact ? contact.id : client.id]);

	if (!loading && total === 0) {
		return null;
	}

	const flowElements = flows.map((flow, index) => (
		<div key={`${flow.entityId}-${index}`}>
			<TimelineGenericRender
				type={TimelineConfig.ActiveFlow}
				event={flow}
				includeIcon
				hasAvatar
				hideLine={index === flows.length - 1 && flows.length >= total}
			/>
		</div>
	));

	return (
		<HistoryLog title={`${T('flow.active.title')} (${total})`} loading={loading} hasPriorHistory>
			{flowElements}
			{flows.length < total ? (
				<TimelineLoadMore
					newEventsLoading={loading && flows.length > 0}
					numEventsLoaded={flows.length}
					totalEvents={total}
					incrementOffset={() => loadFlows(false)}
				/>
			) : null}
		</HistoryLog>
	);
};

export default ActiveFlowHistoryLog;
